/**
 * 공지사항 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { noticeService } from '../../services/noticeService';
import { Notice, NoticeFilter } from '../../types/notice';

const NoticesList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<NoticeFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['notices', filter],
    queryFn: () => noticeService.getNotices(filter),
  });

  const getNoticeTypeBadge = (type: string) => {
    switch (type) {
      case 'urgent':
        return <Badge bg="danger">긴급</Badge>;
      case 'maintenance':
        return <Badge bg="warning">점검</Badge>;
      default:
        return <Badge bg="info">일반</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<Notice>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'title',
      label: '제목',
      render: (notice) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/notices/${notice.id}`)}
        >
          {notice.title.ko || notice.title.en || '-'}
        </span>
      ),
    },
    {
      key: 'notice_type',
      label: '유형',
      render: (notice) => getNoticeTypeBadge(notice.notice_type),
    },
    {
      key: 'priority',
      label: '우선순위',
      render: (notice) => (
        <span style={{ color: '#ccc' }}>{notice.priority}</span>
      ),
    },
    {
      key: 'is_main_visible',
      label: '메인 노출',
      render: (notice) => (
        notice.is_main_visible ? (
          <Badge bg="primary">노출</Badge>
        ) : (
          <Badge bg="secondary">비노출</Badge>
        )
      ),
    },
    {
      key: 'start_date',
      label: '시작일',
      render: (notice) => (
        <span style={{ color: '#ccc' }}>
          {notice.start_date ? new Date(notice.start_date).toLocaleDateString('ko-KR') : '-'}
        </span>
      ),
    },
    {
      key: 'end_date',
      label: '종료일',
      render: (notice) => (
        <span style={{ color: '#ccc' }}>
          {notice.end_date ? new Date(notice.end_date).toLocaleDateString('ko-KR') : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (notice) => getStatusBadge(notice.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '제목, 내용으로 검색',
    },
    {
      name: 'notice_type',
      label: '유형',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'general', label: '일반' },
        { value: 'urgent', label: '긴급' },
        { value: 'maintenance', label: '점검' },
      ],
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
    {
      name: 'is_main_visible',
      label: '메인 노출',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'true', label: '노출' },
        { value: 'false', label: '비노출' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      is_main_visible: values.is_main_visible === 'true' ? true : values.is_main_visible === 'false' ? false : undefined,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (notice: Notice) => {
    navigate(`/admin/notices/${notice.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>공지사항 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/notices/new')}>
            공지사항 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.notices || []}
              loading={isLoading}
              emptyMessage="공지사항이 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default NoticesList;

